import * as React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "../../../assets/icons/closeIcon.svg";
import TextField from "@mui/material/TextField";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginPopupProps from "./login-popup.props";
import "./login-popup.scss";
import MemorialGoLogo from "../../../assets/media/memgo-logo.svg";
import EmailIcon from "../../../assets/icons/email-placeholder.svg";
import LockIcon from "../../../assets/icons/lock-icon.svg";

const LoginPopup: React.FC<LoginPopupProps> = ({
  open,
  onClose,
  onLogin,
  loading = false,
  error,
  headerText,
  bodyText,
  bodyContent,
}) => {
  const initialValues = { email: "", password: "" };
  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Please enter a valid email address")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
        "Please enter a valid email address"
      )
      .required("Email address is required"),
    password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
  });

  const handleSubmit = (values: { email: string; password: string }) => {
    onLogin(values.email, values.password);
  };

  const handleClose = () => {
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="login-dialog-title"
      className="login-popup-wrapper"
    >
      <DialogTitle id="login-dialog-title">
        <div className="header-wrapper">
          <img
            src={MemorialGoLogo}
            alt="MemorialGo Logo"
            className="memorial-go-logo-img"
          />
          <h4 className="header-text">{headerText}</h4>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="body-wrapper">
          <p className="body-text">{bodyText}</p>
          <p className="body-content">{bodyContent}</p>
        </div>
        <div className="form-wrapper">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              isSubmitting,
              isValid,
              dirty,
              values,
              handleChange,
              handleBlur,
              touched,
              errors,
              setFieldValue,
            }) => {
              return (
                <Form>
                  <div className="form-fields">
                    <div className="form-label">
                      Email Address <span className="form-label-required">*</span>
                    </div>
                   <TextField
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email address"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src={EmailIcon} alt="Email Icon" />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      className="form-field"
                    />

                    {touched.email && errors.email && (
                      <div className="error-message">{errors.email}</div>
                    )}

                    <div className="form-label">
                      Password <span className="form-label-required">*</span>
                    </div>
                    <OutlinedInput
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      fullWidth
                      className="form-field"
                      startAdornment={
                        <InputAdornment position="start">
                          <img src={LockIcon} alt="Password Icon" />
                        </InputAdornment>
                      }
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <Visibility/> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      }
                      
                    />
                    {touched.password && errors.password && (
                      <div className="error-message">{errors.password}</div>
                    )}
                    {/* {error && <div className="error-message">{error}</div>} */}
                  </div>
                  <DialogActions>
                    <button
                      className="login-btn"
                      type="submit"
                      disabled={loading || !isValid || !dirty}
                    >
                      {loading ? "Logging in..." : "Login"}
                    </button>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginPopup;
